import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useTheme } from '../context/ThemeContext';
import { cn } from '../lib/utils';
import { 
  MessageSquare, 
  TicketPlus, 
  AlertCircle, 
  UserCheck,
  Activity,
  Bell,
  User,
  Package,
  Settings,
  CheckCircle2
} from 'lucide-react';
import { Button } from './ui/button';

// Notification icons by type
const notificationIcons = {
  ticket_created: TicketPlus,
  status_changed: Activity,
  comment_added: MessageSquare,
  assigned: UserCheck,
  priority_changed: AlertCircle,
  system_alert: Bell,
  user_update: User,
  product_launch: Package,
  settings_change: Settings,
};

// Category-based fallback icons
const categoryIcons = {
  ticket: TicketPlus,
  system: Bell,
  user: User,
  product: Package,
  settings: Settings,
};

export default function NotificationItem({ notification, onClick, onMarkAsRead }) {
  const { isDark } = useTheme();
  
  // Choose icon based on type, then category, then default
  const Icon = notificationIcons[notification.type] 
    || (notification.category && categoryIcons[notification.category])
    || Activity;
  
  const handleMarkAsRead = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent triggering onClick
    if (onMarkAsRead) {
      onMarkAsRead(notification.id);
    }
  };
  
  return (
    <div
      onClick={onClick}
      className={cn(
        "p-4 hover:cursor-pointer transition-colors relative group",
        !notification.is_read && isDark 
          ? "bg-blue-950/40 hover:bg-blue-950/60" 
          : !notification.is_read
          ? "bg-blue-50 hover:bg-blue-100"
          : isDark
          ? "hover:bg-slate-800/50"
          : "hover:bg-slate-50"
      )}
    >
      <div className="flex gap-3">
        <div className={cn(
          "flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center",
          notification.is_read 
            ? isDark 
              ? "bg-slate-700" 
              : "bg-gray-100"
            : isDark
            ? "bg-blue-900/50"
            : "bg-blue-100"
        )}>
          <Icon className={cn(
            "h-5 w-5",
            notification.is_read 
              ? isDark 
                ? "text-slate-400" 
                : "text-gray-500"
              : isDark
              ? "text-blue-400"
              : "text-blue-600"
          )} />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className={cn(
              "text-sm font-medium truncate",
              !notification.is_read && "font-semibold",
              isDark ? "text-slate-100" : "text-slate-900"
            )}>
              {notification.title}
            </h4>
            <div className="flex items-center gap-1 flex-shrink-0">
              {!notification.is_read && (
                <>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={handleMarkAsRead}
                    onMouseDown={(e) => e.stopPropagation()}
                    title="Mark as read"
                  >
                    <CheckCircle2 className={`h-4 w-4 ${isDark ? "text-blue-400" : "text-blue-600"}`} />
                  </Button>
                  <span className={`w-2 h-2 ${isDark ? "bg-blue-400" : "bg-blue-600"} rounded-full`} />
                </>
              )}
            </div>
          </div>
          
          <p className={cn(
            "text-sm mt-1 line-clamp-2",
            isDark ? "text-slate-400" : "text-slate-600"
          )}>
            {notification.message}
          </p>
          
          <div className={cn(
            "flex items-center gap-2 mt-2 text-xs",
            isDark ? "text-slate-400" : "text-slate-500"
          )}>
            {notification.ticket_number && (
              <span className={cn(
                "font-mono px-2 py-0.5 rounded",
                isDark ? "bg-slate-800 text-slate-300" : "bg-gray-100 text-slate-700"
              )}>
                {notification.ticket_number}
              </span>
            )}
            {notification.category && (
              <span className={cn(
                "px-2 py-0.5 rounded text-xs font-medium",
                isDark ? "bg-blue-900/50 text-blue-300" : "bg-blue-50 text-blue-700"
              )}>
                {notification.category}
              </span>
            )}
            <span>
              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
